import React, { useState, useCallback } from 'react';

const RATING_LABELS = {
  1: 'Poor',
  2: 'Fair',
  3: 'Good',
  4: 'Very Good',
  5: 'Excellent'
};

const StarRating = ({ rating, hoverRating, onRate, onHover, onLeave }) => {
  return (
    <div onMouseLeave={onLeave}>
      <div className="flex gap-2 justify-center text-4xl mb-2">
        {[1, 2, 3, 4, 5].map(star => (
          <span
            key={star}
            onClick={() => onRate(star)}
            onMouseEnter={() => onHover(star)}
            className={`cursor-pointer transition-all duration-200 transform hover:scale-110 ${
              star <= (hoverRating || rating) ? 'text-yellow-400' : 'text-gray-300'
            }`}
          >
            ★
          </span>
        ))}
      </div>
      <p className="text-center text-gray-500 text-sm min-h-[20px]">
        {RATING_LABELS[hoverRating || rating] || ''}
      </p>
    </div>
  );
};

const BookReviewForm = () => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [formData, setFormData] = useState({
    review: '',
    name: '',
    title: '',
    email: '',
    phone: ''
  });
  const [agreed, setAgreed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (rating === 0) {
      setError('Please select a star rating.');
      return;
    }
    if (!formData.review.trim()) {
      setError('Please enter your review.');
      return;
    }
    if (!formData.name.trim()) {
      setError('Please enter your name.');
      return;
    }
    if (!formData.title.trim()) {
      setError('Please enter your title/position.');
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!agreed) {
      setError('Please agree to the permission terms.');
      return;
    }

    // Create submission data
    const submissionData = {
      rating,
      review: formData.review,
      name: formData.name,
      title: formData.title,
      email: formData.email,
      phone: formData.phone,
      publicUsePermission: agreed,
      timestamp: new Date().toISOString()
    };

    // Log to console (in production, send to server)
    console.log('Review submitted:', submissionData);

    // Show success
    setSubmitted(true);
  }, [rating, formData, agreed]);

  const resetForm = useCallback(() => {
    setRating(0);
    setHoverRating(0);
    setFormData({
      review: '',
      name: '',
      title: '',
      email: '',
      phone: ''
    });
    setAgreed(false);
    setSubmitted(false);
    setError('');
  }, []);

  // Success screen
  if (submitted) {
    return (
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-8 py-10 text-center">
          <svg className="w-16 h-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h2 className="text-2xl font-bold mb-2">Thank You!</h2>
          <p className="text-green-100">Your review has been submitted successfully.</p>
        </div>

        <div className="p-8 text-center">
          <p className="text-gray-600 mb-6">
            We appreciate you taking the time to share your thoughts about
            "Leadership Continuity for Credit Unions: The NEXT Method"
          </p>
          <button
            onClick={resetForm}
            className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:shadow-lg transform hover:-translate-y-0.5 transition-all"
          >
            Submit Another Review
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white px-8 py-8 text-center">
        <h2 className="text-xl md:text-2xl font-bold mb-2">Leadership Continuity for Credit Unions</h2>
        <p className="text-purple-200">The NEXT Method</p>
        <p className="text-purple-100 text-sm mt-2">Share your review of the book</p>
      </div>

      <div className="p-6 md:p-8">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Star Rating */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-3">
              Rating <span className="text-red-500">*</span>
            </label>
            <StarRating
              rating={rating}
              hoverRating={hoverRating}
              onRate={setRating}
              onHover={setHoverRating}
              onLeave={() => setHoverRating(0)}
            />
          </div>

          {/* Review */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">
              Your Review <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.review}
              onChange={(e) => handleInputChange('review', e.target.value)}
              maxLength={1000}
              placeholder="Share your thoughts about the book..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all resize-y min-h-[120px]"
            />
            <p className={`text-right text-sm mt-1 ${formData.review.length >= 950 ? 'text-red-500' : 'text-gray-500'}`}>
              {formData.review.length} / 1000 characters
            </p>
          </div>

          {/* Name */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Your full name"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all"
            />
          </div>

          {/* Title */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">
              Title / Position <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="e.g., CEO, Board Chair, CFO"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all"
            />
          </div>

          {/* Email */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              placeholder="your.email@example.com"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all"
            />
          </div>

          {/* Phone */}
          <div className="mb-6">
            <label className="block text-gray-700 font-semibold mb-2">
              Phone Number
            </label>
            <input
              type="text"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg text-base focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 transition-all"
            />
          </div>

          {/* Permission Checkbox */}
          <div className="mb-8">
            <div className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-1 w-5 h-5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                />
                <div>
                  <span className="font-semibold text-gray-800 text-sm">
                    Permission for Public Use <span className="text-red-500">*</span>
                  </span>
                  <p className="text-gray-600 text-xs mt-1 leading-relaxed">
                    I grant permission for my review, rating, name, and title to be used publicly for promotional
                    and marketing purposes, including but not limited to: website testimonials, social media posts,
                    promotional materials, book listings, and advertising. I understand that my review may be edited
                    for length or clarity while preserving the original meaning and intent. My contact information
                    (email and phone number) will be kept private and will not be shared publicly.
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-700 text-white py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all"
          >
            Submit Review
          </button>
        </form>
      </div>
    </div>
  );
};

export default BookReviewForm;
